// src/auth/new-password-fields.tsx
//
// The password + confirm-password pair with the live strength checklist, shared
// by BOTH the reset-via-access-code flow and post-invite create-password (Flutter
// `reset_password_new_password` + `create_password` share the same rule set —
// see password-policy.ts). Composes DS `TextField` atoms; the checklist colours
// come from DS status tokens (success = passed, muted = pending). No raw hex.
import { TextField } from '@scripsteam/scrips-signal-ds';
import { evaluatePassword, MAX_LENGTH } from './password-policy';
import { Check } from 'lucide-react';

interface NewPasswordFieldsProps {
  password: string;
  confirmPassword: string;
  onPasswordChange: (value: string) => void;
  onConfirmChange: (value: string) => void;
  disabled?: boolean;
}

/** The password + confirm pair with the live rule checklist. The parent gates
 *  its own submit via `evaluatePassword(password, confirm).canSubmit` — the
 *  policy is a pure function (single source of truth), so there is no callback
 *  and no setState-during-render. */
export function NewPasswordFields({
  password,
  confirmPassword,
  onPasswordChange,
  onConfirmChange,
  disabled,
}: NewPasswordFieldsProps) {
  const evaluation = evaluatePassword(password, confirmPassword);

  const confirmError =
    confirmPassword.length > 0 && !evaluation.matches ? 'Passwords do not match' : undefined;

  return (
    <>
      <TextField
        label="New password"
        type="password"
        value={password}
        onChange={(e) => onPasswordChange(e.target.value)}
        autoComplete="new-password"
        maxLength={MAX_LENGTH}
        disabled={disabled}
      />
      {/* Showcase look (design PasswordRules `list`): one rule per line on the label role, the meta ink while pending
          with an empty 16 ring, the good-status text with a 16 check once passed (`.password-rule*`,
          showcase-auth.css). Markup only: the rules, their order and `passed` come from evaluatePassword. */}
      <ul className="password-rules" aria-label="Password requirements">
        {evaluation.rules.map((rule) => (
          <li key={rule.key} className="password-rule" data-passed={rule.passed ? '' : undefined}>
            {rule.passed ? (
              <Check className="size-4" aria-hidden />
            ) : (
              <span className="password-rule-ring" aria-hidden="true" />
            )}
            {rule.label}
          </li>
        ))}
      </ul>
      <TextField
        label="Confirm password"
        type="password"
        value={confirmPassword}
        onChange={(e) => onConfirmChange(e.target.value)}
        autoComplete="new-password"
        maxLength={MAX_LENGTH}
        error={confirmError}
        disabled={disabled}
      />
    </>
  );
}
